import React from 'react';
import { FormControl, InputV2, TextV2 } from '@withjoy/joykit';
import { StyledContent, StyledFormStack, StyledHeaderStack, StyledInputsStack, StyledTitleStack, styles } from '../UnlockForm/UnlockForm.styles';
import { StyledInputWrapper } from '../shared/UnlockInputWrapper.styles';
import { useAskHostFormController } from './AskHostForm.controller';
import { GuestSiteTypographyOverride } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride';
import { body4ToSubHeadingOverride, hed6ToSubHeadingOverride } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride.constants';
import { useDeferredInputFocus } from '../useDeferredInputFocus';
import { useUnlockDialogInputStyles } from '../useUnlockDialogButtonStyles';
import { UnlockSubmitButton } from '../shared/UnlockSubmitButton';

export interface AskHostFormProps {
  eventId: string;
  onClose: () => void;
  disablePadding?: boolean;
  focusOnMount?: boolean;
}

export const AskHostForm: React.FC<AskHostFormProps> = ({ eventId, onClose, disablePadding = false, focusOnMount = false }) => {
  const { formik, loading, title, subtitle, fullNameLabel, emailLabel, submitButtonText, nameInputError, emailInputError, namePlaceholder, emailPlaceholder } =
    useAskHostFormController({
      eventId,
      onClose,
    });
  const inputRef = useDeferredInputFocus({ focusOnMount });
  const inputStyles = useUnlockDialogInputStyles();

  return (
    <StyledContent __css={disablePadding ? undefined : styles.content}>
      <StyledHeaderStack>
        <StyledTitleStack>
          <GuestSiteTypographyOverride override={hed6ToSubHeadingOverride}>
            <TextV2 typographyVariant="hed6">{title}</TextV2>
          </GuestSiteTypographyOverride>
          <GuestSiteTypographyOverride override={body4ToSubHeadingOverride}>
            <TextV2 typographyVariant="body4">{subtitle}</TextV2>
          </GuestSiteTypographyOverride>
        </StyledTitleStack>
      </StyledHeaderStack>
      <StyledFormStack
        spacing={6}
        __css={styles.formStack}
      >
        <form onSubmit={formik.handleSubmit}>
          <StyledInputsStack>
            <StyledInputWrapper $focusColor={inputStyles.focusColor}>
              <FormControl
                label={fullNameLabel}
                overrides={{ Label: { props: { color: 'inherit' } } }}
              >
                <InputV2
                  ref={inputRef}
                  overrides={{ Root: { props: { borderColor: inputStyles.borderColor } } }}
                  placeholder={namePlaceholder}
                  isInvalid={(nameInputError && formik.touched.name) || undefined}
                  isDisabled={loading}
                  {...formik.getFieldProps('name')}
                />
              </FormControl>
            </StyledInputWrapper>
            <StyledInputWrapper $focusColor={inputStyles.focusColor}>
              <FormControl
                label={emailLabel}
                overrides={{ Label: { props: { color: 'inherit' } } }}
              >
                <InputV2
                  overrides={{ Root: { props: { borderColor: inputStyles.borderColor } } }}
                  placeholder={emailPlaceholder}
                  isInvalid={(emailInputError && formik.touched.email) || undefined}
                  isDisabled={loading}
                  {...formik.getFieldProps('email')}
                />
              </FormControl>
            </StyledInputWrapper>
          </StyledInputsStack>
          <UnlockSubmitButton
            width={'100%'}
            shape="rounded"
            variant="outline"
            type="submit"
            loading={loading}
          >
            {submitButtonText}
          </UnlockSubmitButton>
        </form>
      </StyledFormStack>
    </StyledContent>
  );
};
